import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CampaignContext } from "../Context";
import InboxPreview from "./InboxPreview";
import CustomTextInput from "global/components/CustomTextInput/CustomTextInput";
import CustomAutoCompleteNew from "global/components/CustomAutoCompleteNew/CustomAutoCompleteNew";
import { fetchSubordinates } from "screens/Shared/CommonService";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import { cloneDeep } from "lodash";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { fetchApprovedDomains } from "screens/Campaigns/CampaignService";

const FromSection = () => {
  const {
    managingStep,
    setManagingStep,
    submitCampaign,
    setSubmitCampaign,
    saveAsDraft,
  } = useContext(CampaignContext);
  const [subordinates, setSubordinates] = useState([]);
  const [formFields, setFormFields] = useState({
    displayName: {
      value: submitCampaign.campaign.displayName ?? "",
      error: "",
    },
    fromId: {
      value: submitCampaign.campaign.fromId ?? 0,
      error: "",
    },
    fromIdEmail: {
      value: submitCampaign.campaign.fromIdEmail ?? "",
      error: "",
    },
    domainName: {
      value: submitCampaign.campaignData.domainName ?? "",
      error: "",
    },
  });
  const [approvedDomains, setApprovedDomains] = useState([]);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (managingStep === "from-section") {
      retrieveSubordinates();
      fetchDomains();
    }
  }, [managingStep]);

  const retrieveSubordinates = async () => {
    try {
      const subordinates = await fetchSubordinates();
      setSubordinates(
        subordinates.map((subordinate: any) => {
          return {
            label: subordinate.email,
            value: subordinate.userId,
          };
        })
      );
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await fetchApprovedDomains();
      setApprovedDomains(response);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleCancel = () => {
    setManagingStep("");
    setFormFields({
      displayName: {
        value: submitCampaign.campaign.displayName ?? "",
        error: "",
      },
      fromId: {
        value: submitCampaign.campaign.fromId ?? 0,
        error: "",
      },
      fromIdEmail: {
        value: submitCampaign.campaign.fromIdEmail ?? "",
        error: "",
      },
      domainName: {
        value: submitCampaign.campaignData.domainName ?? "",
        error: "",
      },
    });
  };

  const handleSave = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.displayName.value)) {
      error = true;
      clonedFormFields.displayName.error = "Please enter the display name";
    }
    if (
      !isTruthy(clonedFormFields.fromId.value) ||
      clonedFormFields.fromId.value === 0
    ) {
      error = true;
      clonedFormFields.fromId.error =
        "Please select from the from email address";
    }
    if (!isTruthy(clonedFormFields.domainName.value)) {
      error = true;
      clonedFormFields.domainName.error = "Please select the domain";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    setSaving(true);
    const campaign = {
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        fromId: formFields.fromId.value,
        displayName: formFields.displayName.value,
        fromIdEmail: formFields.fromIdEmail.value,
      },
      campaignData: {
        ...submitCampaign.campaignData,
        domainName: formFields.domainName.value,
      },
    };
    setSubmitCampaign(campaign);
    await saveAsDraft(campaign);
    setManagingStep("");
    setSaving(false);
  };

  const stepCompletion = () => {
    return (
      isTruthy(submitCampaign.campaign.displayName) &&
      isTruthy(submitCampaign.campaign.fromId) &&
      isTruthy(submitCampaign.campaignData.domainName)
    );
  };

  const getExpandedView = () => {
    return (
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={4} width="100%">
            <SvgIcon>
              <CheckCircleIcon
                htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
              />
            </SvgIcon>
            <Stack spacing={2} width="100%">
              <Stack spacing={0.5}>
                <Typography variant="h4">From</Typography>
                <Typography variant="body1">
                  Define who's sending the campaign for recipient trust
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <CustomTextInput
                      label="Name"
                      value={formFields.displayName.value}
                      required
                      placeHolder="Enter the from name"
                      onChange={(event: any) => {
                        setFormFields({
                          ...formFields,
                          displayName: {
                            error: "",
                            value: event.target.value,
                          },
                        });
                      }}
                      error={formFields.displayName.error}
                      propsToInputElement={{ minLength: 1, maxLength: 90 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <span style={{ fontSize: "0.75rem" }}>
                              {formFields.displayName?.value?.length} / 90
                            </span>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <CustomAutoCompleteNew
                      options={subordinates}
                      renderOption={(props, option) => {
                        return <li {...props}>{option.label}</li>;
                      }}
                      id={"from-section-email-address"}
                      onChange={(
                        event: any,
                        newValue: { label: string; value: string }
                      ) => {
                        setFormFields({
                          ...formFields,
                          fromId: {
                            error: "",
                            value: newValue?.value
                              ? parseInt(newValue?.value)
                              : 0,
                          },
                          fromIdEmail: {
                            error: "",
                            value: newValue?.label ? newValue?.label : "",
                          },
                        });
                      }}
                      value={formFields.fromIdEmail.value}
                      label="Email Address"
                      placeHolder="Select the email address"
                      error={formFields.fromId.error}
                    />
                    {approvedDomains.length > 1 && (
                      <Stack>
                        <Box mb={1}>
                          <InputLabel required>Domain</InputLabel>
                        </Box>
                        <Select
                          value={formFields.domainName.value}
                          error={isTruthy(formFields.domainName.error)}
                          onChange={(event: any) => {
                            setFormFields({
                              ...formFields,
                              domainName: {
                                error: "",
                                value: event.target.value,
                              },
                            });
                          }}
                          renderValue={
                            formFields.domainName.value !== ""
                              ? undefined
                              : () => (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Select domain
                                  </Typography>
                                )
                          }
                          displayEmpty
                          sx={{
                            "& .MuiInputBase-input": {
                              padding: "10px 8px",
                              color: "#475569",
                            },
                            "& .MuiSelect-root": {
                              border: "none !important",
                              padding: "0.4375rem !important",
                              height: "2.5rem",
                              borderRadius: "0.5rem",
                            },
                          }}
                        >
                          {approvedDomains.map((domain: string) => (
                            <MenuItem key={domain} value={domain}>
                              {domain}
                            </MenuItem>
                          ))}
                        </Select>
                        {!isTruthy(formFields.domainName.value) && (
                          <FormHelperText error>
                            {formFields.domainName.error}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InboxPreview
                    for="from"
                    string={formFields.displayName.value}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Stack>
          <Stack>
            <IconButton onClick={handleCancel}>
              <SvgIcon>
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={1}
        >
          <Button size="small" variant="text" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getNonExpandedView = () => {
    const onAnotherStep =
      managingStep !== "" && managingStep !== "from-section";
    return (
      <Grid
        container
        gap={1}
        justifyContent="space-between"
        sx={{ opacity: onAnotherStep ? 0.5 : 1 }}
      >
        <Stack direction="row" spacing={4}>
          <SvgIcon>
            <CheckCircleIcon
              htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
            />
          </SvgIcon>
          <Stack spacing={0.5}>
            <Typography variant="h4">From</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Tooltip title={submitCampaign.campaign.displayName} arrow>
                <Typography
                  variant="subtitle1"
                  noWrap
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxWidth="250px"
                  display="block"
                >
                  {submitCampaign.campaign.displayName}
                </Typography>
              </Tooltip>

              <Typography variant="body2" color="text.secondary">
                &#x25CF;
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {submitCampaign.campaign.fromIdEmail}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setManagingStep("from-section");
            }}
            disabled={onAnotherStep}
          >
            Edit From
          </Button>
        </Stack>
      </Grid>
    );
  };

  const getPage = () => {
    if (managingStep === "from-section") {
      return getExpandedView();
    }
    return getNonExpandedView();
  };

  return (
    <>
      <Box px={3}>{getPage()}</Box>
      <CustomLoader isLoading={saving} />
    </>
  );
};

export default FromSection;
